<script lang="ts">
  import { Button } from "$lib/components/ui/button";
  import * as Select from "$lib/components/ui/select";
  import { cn } from "$lib/utils";
  import { fatCheckIcon } from "$lib/images/icons";
  import { SVG } from "$lib/components";
  import { createEventDispatcher } from "svelte";
  import { goto } from "$app/navigation";
  import { authStore } from "$lib/stores";
  import { Plane } from "lucide-svelte";

  interface SubTier {
    label: string;
    price: number;
  }

  interface ServerPlan {
    name: string;
    active: boolean;
    recurringCredits: number;
  }

  let selectedTier = $state<(SubTier & { recurringCredits: number }) | null>(null);
  let currentPrice = $state(0);
  let selectedValue = $state("");
  let mergedTiers = $state<(SubTier & { recurringCredits: number })[]>([]);

  let {
    title = "Title",
    icon = undefined,
    variant = "default",
    buttonClass = "",
    displayedPrice = 0,
    description = "",
    benefits = [""],
    buyable = true,
    actionable = true,
    isSignInPrompt = false,
    isUpgradeable = false,
    subtiers = [] as SubTier[],
    serverPlans = [] as ServerPlan[],
    onUpgrade = null as ((tierLabel: string) => void) | null,
    onAction = undefined as ((selectedLabel: string) => void) | undefined,
    billingPeriod = "monthly" as "monthly" | "annual",
    currentPlanLabel = "",
    cardClass = "",
  } = $props();

  let isCurrentPlan = $derived(
    selectedTier?.label === currentPlanLabel || (serverPlans.length === 0 && currentPlanLabel === ""),
  );

  const dispatch = createEventDispatcher();

  // Effect 1: Filter and merge tiers based on billing period
  $effect(() => {
    if (serverPlans.length > 0 && subtiers?.length > 0) {
      const filteredTiers = subtiers.filter((tier) => tier.label.includes(`_${billingPeriod}`));

      mergedTiers = filteredTiers.map((tier) => {
        const serverPlan = serverPlans.find((sp) => sp.name === tier.label);
        return {
          ...tier,
          recurringCredits: serverPlan?.recurringCredits || 0,
        };
      });

      if (title === "Free") {
        mergedTiers = [{ label: "free_monthly_tier_1", price: 0, recurringCredits: 1000 }];
      }
    }
  });

  // Effect 2: Initialize or update selected tier
  $effect(() => {
    logger.log("effect 2", title);
    if (mergedTiers?.length > 0) {
      // If no tier is selected yet, handle initialization
      if (!selectedTier) {
        if (currentPlanLabel) {
          // Try to match current plan first
          const userPlanTier = mergedTiers.find((tier) => tier.label === currentPlanLabel);
          if (userPlanTier) {
            logger.log("effect -- A: Initial selection with user plan");
            selectedTier = userPlanTier;
            currentPrice = userPlanTier.price;
            selectedValue = userPlanTier.label;
            return;
          }
        }
        // No current plan or not found - default to first tier
        logger.log("effect -- C: Initial selection with first tier");
        selectedTier = mergedTiers[0];
        currentPrice = mergedTiers[0].price;
        selectedValue = mergedTiers[0].label;
        return;
      }

      // Handle billing period change only if current selection is invalid
      if (!mergedTiers.find((t) => t.label === selectedTier?.label)) {
        logger.log("effect -- B: Billing period change");
        const currentTierBase = selectedTier.label.replace(/_monthly|_annual/, "");
        const matchingTier = mergedTiers.find((t) => t.label.startsWith(currentTierBase));
        if (matchingTier) {
          selectedTier = matchingTier;
          currentPrice = matchingTier.price;
          selectedValue = matchingTier.label;
        }
      }
    } else {
      currentPrice = displayedPrice;
    }
  });

  function formatTierLabel(tier: SubTier & { recurringCredits: number }): string {
    let postfix = billingPeriod === "monthly" ? "/ month" : "/ year";
    if (title === "Team") {
      postfix += ` / team`;
    }
    return `${tier.recurringCredits.toLocaleString()} credits ${postfix}`;
  }

  function handleValueChange(value: string) {
    const newTier = mergedTiers.find((tier) => tier.label === value);
    if (newTier) {
      selectedTier = newTier;
      currentPrice = newTier.price;
      selectedValue = value;
    }
  }

  const cardClasses = cn(
    "flex text-black flex-col mx-auto xl:max-w-96 !rounded-base py-6 px-8 w-full transition-default shadow-sm",
    "dark:border-white/20 border-1",
    variant === "highlight"
      ? "bg-theme-green shadow shadow-xl border border-black"
      : variant === "dark"
        ? "bg-black text-white"
        : "bg-white border border-blog-gray-200 dark:border-black/10",
  );

  function getActionButton() {
    if (!actionable) return null;

    if (isSignInPrompt) {
      return {
        text: "Get Started",
        props: {
          variant: "primary",
          class: cn(
            "!rounded-base w-full font-normal text-base transition-default",
            variant === "highlight"
              ? "bg-black text-white"
              : "text-black bg-theme-green hover:bg-theme-green border border-transparent hover:border-black",
            buttonClass,
          ),
          onClick: () => {
            if (typeof onAction === "function") {
              onAction(selectedTier?.label || "");
            }
            goto("/sign_up");
          },
        },
      };
    }

    if (!buyable && !isCurrentPlan) {
      return {
        text: "Coming Soon",
        props: {
          variant: "primary",
          disabled: true,
          class: "w-full md:w-fit border-black bg-white text-black hover:bg-white border pointer-events-none",
          onClick: () => {
            dispatch("closeDialog");
          },
        },
      };
    }

    if (isCurrentPlan) {
      return {
        text: "Your current plan",
        props: {
          variant: "primary",
          disabled: true,
          class: cn(
            "!rounded-base w-full font-normal text-base transition-default",
            variant === "highlight"
              ? "bg-black text-white"
              : "text-black bg-white hover:bg-theme-green border border-black hover:border-black",
          ),
        },
      };
    }

    if (isUpgradeable) {
      return {
        text: "Upgrade now",
        props: {
          variant: "primary",
          outline: true,
          class: cn(
            "!rounded-base w-full font-normal text-base transition-default",
            variant === "highlight"
              ? "bg-black text-white"
              : "text-black bg-theme-green hover:bg-theme-green border border-transparent hover:border-black",
            buttonClass,
          ),
          onClick: () => onUpgrade?.(selectedTier?.label === "free_monthly_tier_1" ? "" : selectedTier?.label || ""),
        },
      };
    }

    return null;
  }

  let textColor = $derived(
    variant === "highlight" ? "text-black" : variant === "dark" ? "text-white" : "text-[#331301]",
  );

  // Flag to show special price display elements
  let isTeamPlan = $derived(title === "Team");
  let isFreePlan = $derived(title === "Free");

  // For Team plan - calculate individual and team prices
  let teamPrice = $derived(isTeamPlan ? selectedTier?.price || displayedPrice : 0);
  let perPersonPrice = $derived(isTeamPlan ? teamPrice / 5 : 0); // min 5 people per team

  // Credits amount for Free plan
  let freeCredits = $derived(isFreePlan ? 1000 : 0);

  // Display price logic
  let displayPrice = $derived(isFreePlan ? 0 : isTeamPlan ? perPersonPrice : currentPrice);

  let actionButton = $derived(getActionButton());
</script>

<div class={cn(cardClasses, cardClass)}>
  <div class="mb-12 flex flex-col justify-stretch gap-3">
    <h2 class={cn("text-[32px] font-normal", textColor)}>{title}</h2>
    <span>
      <span class="text-[58px] font-normal leading-normal">
        ${displayPrice}
      </span>
      <span class={cn(variant === "highlight" ? "text-black" : variant === "dark" ? "text-white" : "text-[#331301]")}>
        {#if isTeamPlan}
          / month for one person
        {:else}
          / month
        {/if}
      </span>
      {#if billingPeriod === "annual" && currentPrice !== 0}
        <span class="text-xs">(billed annually)</span>
      {/if}
    </span>

    {#if isTeamPlan}
      <span class="text-base">Minimum 5 people (${teamPrice} / month / team)</span>
    {/if}

    {#if isFreePlan}
      <span class="text-base">500 credits on Sign Up + First Template Run for Free</span>
    {/if}

    {#if mergedTiers?.length > 0 && title !== "Free"}
      <Select.Root type="single" value={selectedValue} onValueChange={handleValueChange} allowDeselect={false}>
        <Select.Trigger class={cn("w-full", "!rounded-base border-white bg-white text-black")}>
          {selectedTier ? formatTierLabel(selectedTier) : "Select credits tier"}
        </Select.Trigger>
        <Select.Content class="!rounded-base">
          {#each mergedTiers as tier}
            <Select.Item value={tier.label} class="cursor-pointer">
              {formatTierLabel(tier)}
            </Select.Item>
          {/each}
        </Select.Content>
      </Select.Root>
    {/if}
  </div>

  <div class="flex-grow">
    <div class="flex flex-col">
      <ul role="list" class={cn("space-y-5 text-base font-light", textColor)}>
        {#if description}
          <li class={cn("flex gap-x-3 text-[18px] tracking-tight", textColor)}>
            {description}
          </li>
        {/if}
        {#each benefits as benefit}
          <li class="flex gap-x-3">
            <SVG
              file={fatCheckIcon}
              className={cn(
                "mt-1.5",
                variant === "highlight" ? "text-black" : variant === "dark" ? "text-white" : "text-[#B6B6B6]",
              )}
            />
            <span>{benefit}</span>
          </li>
        {/each}
      </ul>
    </div>
  </div>

  {#if actionButton}
    <div class="mt-12 flex flex-col items-center gap-6 md:items-start">
      <Button
        class={actionButton.props.class}
        onclick={actionButton.props.onClick}
        disabled={actionButton.props.disabled}
        variant={actionButton.props.variant as any}
      >
        {actionButton.text}
      </Button>
    </div>
  {/if}
</div>
